import {
    autoUpdate,
    flip,
    Placement,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useListNavigation,
    useRole,
    useTypeahead
} from "@floating-ui/react";
import { cn } from "@omni/utils/styles";
import { ReactNode, useCallback, useRef, useState } from "react";

import { DropdownContextProvider } from "@/components/General/Dropdown/context/dropdown.context";

type DropdownBaseProps = {
    placement: Placement;
    isLabel: boolean;
    className?: string;
    children: ReactNode;
};

export const DropdownBase = ({
    placement,
    isLabel,
    className,
    children
}: DropdownBaseProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

    const { refs, floatingStyles, context } = useFloating({
        placement: placement,
        open: isOpen,
        onOpenChange: setIsOpen,
        whileElementsMounted: autoUpdate,
        middleware: [flip()]
    });

    const elementsRef = useRef<Array<HTMLElement | null>>([]);
    const labelsRef = useRef<Array<string | null>>([]);

    const handleOnSelect = useCallback((index: number | null) => {
        setSelectedIndex(index);
        setIsOpen(false);
        if (index !== null) {
            setSelectedLabel(labelsRef.current[index]);
        }
    }, []);

    const handleOnTypeaheadMatch = (index: number | null) => {
        if (isOpen) {
            setActiveIndex(index);
        } else {
            handleOnSelect(index);
        }
    };

    const listNav = useListNavigation(context, {
        listRef: elementsRef,
        activeIndex,
        selectedIndex,
        onNavigate: setActiveIndex
    });
    const typeahead = useTypeahead(context, {
        listRef: labelsRef,
        activeIndex,
        selectedIndex,
        onMatch: handleOnTypeaheadMatch
    });
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "listbox" });

    const { getReferenceProps, getFloatingProps, getItemProps } =
        useInteractions([listNav, typeahead, click, dismiss, role]);

    return (
        <DropdownContextProvider
            activeIndex={activeIndex}
            selectedIndex={selectedIndex}
            placement={placement}
            isLabel={isLabel}
            isOpen={isOpen}
            label={selectedLabel}
            elementsRef={elementsRef}
            labelsRef={labelsRef}
            floatingStyles={floatingStyles}
            floatingContext={context}
            onSelect={handleOnSelect}
            getToggleProps={getReferenceProps}
            getFloatingProps={getFloatingProps}
            getItemProps={getItemProps}
            setToggleRef={refs.setReference}
            setFloatingRef={refs.setFloating}
        >
            <div className={cn(className, "tw:relative")}>{children}</div>
        </DropdownContextProvider>
    );
};
